import React from "react";
import { Card } from "@/components/UI";
import { CheckCircle, ShoppingCart, Package, Truck, ClipboardList, Boxes, History, Image as ImageIcon } from "lucide-react";
import { Section, KvGrid, SmallTable } from "@/pages/TraceabilitySearch";

// Ortak sipariş izlenebilirlik görünümü (internal + public). Finansal alan YOK — API zaten göndermez.
const fmtDt = (s, dateOnly = false) => {
  if (!s) return "—";
  const d = new Date(s.length === 10 ? `${s}T00:00:00` : s);
  if (Number.isNaN(d.getTime())) return s;
  return dateOnly ? d.toLocaleDateString("tr-TR") : d.toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" });
};
const q = (v) => Number(v || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 });

function Timeline({ rows }) {
  return (
    <ol className="relative border-l-2 border-indigo-100 ml-2 space-y-4" data-testid="trace-timeline">
      {rows.map((e, i) => (
        <li key={i} className={`ml-5 ${e.highlight ? "bg-amber-50 -ml-1 pl-6 py-1 rounded" : ""}`} data-testid={`trace-event-${e.kind}`}>
          <span className="absolute -left-[9px] mt-0.5 w-4 h-4 rounded-full bg-white border-2 border-emerald-500 flex items-center justify-center">
            <CheckCircle size={10} className="text-emerald-600" />
          </span>
          <div className="text-sm font-semibold text-slate-800">{e.title}</div>
          <div className="text-xs text-slate-500">{fmtDt(e.at)}{e.who ? ` · ${e.role ? `${e.role}: ` : ""}${e.who}` : ""}</div>
          {e.note && <div className="text-xs text-slate-600 mt-0.5">{e.note}</div>}
        </li>
      ))}
    </ol>
  );
}

function Shipments({ rows, photoUrl }) {
  return (
    <div className="space-y-3" data-testid="trace-shipments">
      {rows.map((s) => (
        <Card key={s.shipment_no} className={`p-3 ${s.highlight ? "ring-2 ring-amber-400" : ""}`} data-testid={`trace-shipment-${s.shipment_no}`}>
          <div className="flex flex-wrap justify-between text-sm font-semibold"><span>SEVKİYAT {s.shipment_no}</span><span className="text-slate-500 font-normal text-xs">{fmtDt(s.shipped_at)}</span></div>
          <ul className="text-xs text-slate-700 mt-1">{s.lines.map((l, i) => <li key={i}>{l.product_name}: {q(l.qty)} adet</li>)}</ul>
          <div className="text-[11px] text-slate-500 mt-1">Hazırlayan: {s.prepared_by || "—"} · Sevk Eden: {s.shipped_by || "—"}{s.carrier ? ` · ${s.carrier}` : ""}{s.tracking_no ? ` · Takip: ${s.tracking_no}` : ""}</div>
          {s.photos.length > 0 && photoUrl && (
            <div className="flex gap-2 mt-2 flex-wrap">
              {s.photos.map((p) => <img key={p.ref} src={photoUrl(p.ref)} alt="Sevkiyat görseli" className="h-20 w-20 object-cover rounded border" data-testid={`trace-photo-${p.ref}`} />)}
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}

export function SoTraceView({ data, photoUrl, internal = false }) {
  const o = data.order;
  return (
    <div className="space-y-4" data-testid="so-trace-view">
      <Section icon={<ShoppingCart size={14} />} title="Sipariş">
        <KvGrid rows={[["Sipariş No", <span data-testid="trace-so-no">{o.so_no}</span>], ["Müşteri", o.customer_name], ["Sipariş Tarihi", fmtDt(o.order_date, true)],
          ["Müşteri Ref.", o.customer_ref], ["Sipariş Durumu", <span data-testid="trace-status">{o.status_label}</span>], ["Teslimat Durumu", <span data-testid="trace-delivery-status">{o.delivery_status}</span>],
          ["Kaynak", o.source_label], ["Oluşturan", data.people.created_by], ["Oluşturma", fmtDt(data.people.created_at)],
          ["Onaylayan", data.people.approved_by], ["Onay", fmtDt(data.people.approved_at)]]} />
      </Section>
      <Section icon={<Package size={14} />} title="Ürünler">
        <SmallTable testid="trace-lines" columns={["Ürün", "Kod", "Sipariş", "Bedelsiz", "Toplam Fiziksel", "Teslim Edilen", "Kalan"]}
          rows={data.lines.map((l) => [l.product_name, l.product_code, q(l.qty), l.free_qty ? `${q(l.free_qty)}${l.mf_ratio ? ` (MF ${l.mf_ratio})` : ""}` : "—", q(l.physical_qty), q(l.delivered_qty), q(l.remaining_qty)])} />
      </Section>
      <Section icon={<History size={14} />} title="Süreç"><Timeline rows={data.timeline} /></Section>
      {data.reservations.length > 0 && (
        <Section icon={<Boxes size={14} />} title="Stok / Rezervasyon">
          <SmallTable testid="trace-reservations" columns={["Ürün", "Rezerve", "Tarih", "Durum"]} rows={data.reservations.map((r) => [r.product_name, q(r.qty), fmtDt(r.reserved_at), r.status])} />
          {internal && (data.stock_check || []).length > 0 && (
            <div className="mt-2"><SmallTable testid="trace-stock-check" columns={["Ürün", "Gereken", "Kullanılabilir", "Yeterli"]}
              rows={data.stock_check.map((s) => [s.product_name, q(s.required), q(s.available), s.ok ? "Evet" : "Hayır"])} /></div>
          )}
        </Section>
      )}
      {data.purchases.length > 0 && (
        <Section icon={<ClipboardList size={14} />} title="Satın Alma / Mal Kabul">
          {data.purchases.map((p) => (
            <div key={p.po_no} className="mb-3" data-testid={`trace-po-${p.po_no}`}>
              <div className="text-xs font-semibold mb-1">{p.po_no} · {p.supplier} · Sipariş: {fmtDt(p.ordered_at, true)}</div>
              <SmallTable columns={["Ürün", "Sipariş Edilen", "Gelen"]} rows={p.lines.map((l) => [l.product_name, q(l.qty), q(l.received_qty)])} />
              {p.receipts.map((r, i) => <div key={i} className="text-[11px] text-slate-500 mt-1">Mal kabul {fmtDt(r.received_at)} · {r.received_by || "—"} · {r.items.map((it) => `${it.product_name}: ${q(it.qty)}`).join(", ")}</div>)}
            </div>
          ))}
        </Section>
      )}
      {data.lots.length > 0 && (
        <Section icon={<Boxes size={14} />} title="Lot">
          <SmallTable testid="trace-lots" columns={["Lot No", "Ürün", "Üretim", "SKT", "Mal Kabul", "Miktar"]}
            rows={data.lots.map((l) => [l.lot_no, l.product_name, fmtDt(l.mfg_date, true), fmtDt(l.expiry_date, true), fmtDt(l.received_at), q(l.qty)])} />
        </Section>
      )}
      {data.shipments.length > 0 && (
        <Section icon={<Truck size={14} />} title="Sevkiyatlar"><Shipments rows={data.shipments} photoUrl={photoUrl} /></Section>
      )}
      {internal && (
        <>
          <Section icon={<History size={14} />} title="Kullanıcı İşlemleri">
            <SmallTable testid="trace-audit" columns={["Tarih", "İşlem", "Kullanıcı"]} rows={(data.audit || []).map((a) => [fmtDt(a.at), a.action, a.who])} />
          </Section>
          {(data.stock_moves || []).length > 0 && (
            <Section icon={<ImageIcon size={14} />} title="Stok Hareketleri">
              <SmallTable testid="trace-stock-moves" columns={["Tarih", "Hareket", "Ürün", "Miktar", "Açıklama", "Kullanıcı"]}
                rows={data.stock_moves.map((m) => [fmtDt(m.at), m.kind, m.product_name, q(m.qty), m.description, m.who])} />
            </Section>
          )}
        </>
      )}
    </div>
  );
}
